/**
 * Activity: the account's whole history, as one timeline (Customer 360, 02 §5.4)
 * — what the team recorded, promises and how they ended, cheques, and the bills
 * TallyPrime shows as cleared — with the six filters, a search of its own words,
 * and the older pages on request. Receipts are not read from TallyPrime, so none
 * is shown; reminders sent are not recorded yet (B2), so none is invented.
 *
 * Not a tab (owner, 24 Oct 2026): the rail keeps the recent activity on screen,
 * and "View full activity" opens this at the account's own address. It takes the
 * whole workspace, the rail would only repeat it; Record outcome brings the rail
 * back beside it, as the composer.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState, IconArrowLeft, IconSearch, Segmented, fieldClass } from '@timely/ui';
import { TIMELINE_GROUPS, timelineOf, type TimelineGroup } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { useAccount } from './accountPage';
import { useTabTo } from './AccountTabs';
import { TimelineDays } from './Timeline';

export function CustomerActivity() {
    const page = useAccount();
    const tabTo = useTabTo();
    const { count } = useFormatters();
    const [group, setGroup] = useState<TimelineGroup | 'all'>('all');
    const [query, setQuery] = useState('');
    const words = query.trim().toLowerCase();
    const events = timelineOf(page.timeline, group).filter(e => !words
        || [e.title, e.actor ?? '', e.body ?? '', e.amountNote ?? ''].some(t => t.toLowerCase().includes(words)));
    return (
        <div>
            <section aria-labelledby="activity-title" className="rounded-card border border-separator bg-card shadow-e0">
                <div className="px-5 pt-4 flex items-center justify-between gap-x-4 gap-y-3 flex-wrap min-h-14">
                    <div className="flex items-baseline gap-3 min-w-0">
                        <h3 id="activity-title" className="font-display stretch-title text-heading font-semibold text-label tracking-heading">All activity</h3>
                        <Link to={tabTo('overview')} className="inline-flex items-center gap-1 text-secondary font-semibold text-accent-text hover:underline whitespace-nowrap">
                            <IconArrowLeft size={13} />Back to overview
                        </Link>
                    </div>
                    <Segmented label="Show" value={group} onChange={setGroup} className="flex-wrap" options={TIMELINE_GROUPS} />
                </div>
                <div className="px-5 pt-3 flex items-center gap-x-4 gap-y-2 flex-wrap">
                    <label htmlFor="activity-search" className="sr-only">Search this account’s history</label>
                    <div className="relative w-full max-w-sm">
                        <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-label-3"><IconSearch size={14} /></span>
                        <input id="activity-search" type="search" value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Search this account’s history" className={fieldClass({ className: 'w-full pl-9' })} />
                    </div>
                    <p className="text-caption text-label-3 min-w-0 flex-1 basis-72">
                        Newest first. Receipts are not read from TallyPrime; nobody edits what a colleague wrote.
                    </p>
                </div>
                {/* A readable measure: on a wide screen an amount stays beside its event, not across the room from it. */}
                <div className="px-5 pb-4 max-w-4xl">
                    {events.length > 0 ? <TimelineDays events={events} /> : (
                        <EmptyState compact
                            title={page.timeline.length === 0 ? 'Nothing recorded yet' : 'Nothing matches'}
                            hint={page.timeline.length === 0
                                ? (page.rights.record ? 'Record what happens when you ring, and whoever picks this account up next will know what was tried.' : 'Nobody has written anything about this account yet.')
                                : `None of the ${count(page.timeline.length)} entries loaded matches.${page.moreHistory ? ' Older entries may: show them below.' : ''}`} />
                    )}
                </div>
                {page.moreHistory && (
                    <div className="px-5 py-3 border-t border-separator">
                        <Button variant="secondary" size="sm" busy={page.loadingOlder} onClick={() => { void page.loadOlder(); }}>
                            {page.loadingOlder ? 'Loading…' : 'Show older'}
                        </Button>
                    </div>
                )}
            </section>
        </div>
    );
}
